import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./components/ui/table";
import { Button } from "./components/ui/button";
import {
  Search, ChevronLeft, ChevronRight, ArrowDown, ArrowUp, Minus,
  Package, FileText, TrendingDown, TrendingUp, Download, AlertTriangle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MOVEMENT_COLORS = {
  101: { bg: "bg-slate-100", text: "text-slate-700", label: "Opening" },
  102: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Adjustment" },
  103: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Receipt" },
  201: { bg: "bg-rose-100", text: "text-rose-700", label: "Consumption" },
  205: { bg: "bg-blue-100", text: "text-blue-700", label: "Sales Return" },
  206: { bg: "bg-orange-100", text: "text-orange-700", label: "Expired" },
  207: { bg: "bg-red-100", text: "text-red-700", label: "Damage" },
  208: { bg: "bg-purple-100", text: "text-purple-700", label: "Purchase Return" },
  209: { bg: "bg-pink-100", text: "text-pink-700", label: "Write Off" },
  301: { bg: "bg-cyan-100", text: "text-cyan-700", label: "Dept Transfer" },
  302: { bg: "bg-indigo-100", text: "text-indigo-700", label: "Interdept Transfer" },
};

const formatCurrency = (v) => (v || 0).toLocaleString("en-IN");

export default function StockLedger() {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [movementType, setMovementType] = useState("");
  const [data, setData] = useState([]);
  const [kpis, setKpis] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [viewMode, setViewMode] = useState("ledger"); // "ledger" | "deficit"
  const [deficitData, setDeficitData] = useState([]);
  const [deficitTotal, setDeficitTotal] = useState(0);
  const [deficitPage, setDeficitPage] = useState(1);
  const [deficitLoading, setDeficitLoading] = useState(false);

  // Item search autocomplete
  useEffect(() => {
    if (searchQuery.length < 2) { setSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data: items } = await axios.get(`${API}/api/inventory/stock-ledger/items`, { params: { q: searchQuery, limit: 15 } });
        setSuggestions(items);
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchLedger = useCallback(async (pg = 1) => {
    if (!selectedItem) return;
    setLoading(true);
    try {
      const params = { item_code: selectedItem.itemCode, page: pg, page_size: pageSize };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (movementType) params.movement_type = movementType;
      const { data: res } = await axios.get(`${API}/api/inventory/stock-ledger`, { params });
      setData(res.data || []);
      setTotal(res.total || 0);
      setKpis(res.kpis || {});
      setPage(pg);
    } catch (e) {
      toast.error("Failed to load stock ledger");
    } finally {
      setLoading(false);
    }
  }, [selectedItem, dateFrom, dateTo, movementType, pageSize]);

  useEffect(() => { if (selectedItem) fetchLedger(1); }, [fetchLedger, selectedItem]);

  const handleExport = () => {
    if (!selectedItem) return;
    const params = new URLSearchParams({ item_code: selectedItem.itemCode });
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (movementType) params.set("movement_type", movementType);
    window.open(`${API}/api/inventory/stock-ledger/export?${params.toString()}`, "_blank");
  };

  const selectItem = (item) => {
    setSelectedItem(item);
    setSearchQuery(item.itemName);
    setShowSuggestions(false);
    setViewMode("ledger");
  };

  const fetchDeficit = useCallback(async (pg = 1) => {
    setDeficitLoading(true);
    try {
      const params = { page: pg, page_size: pageSize };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: res } = await axios.get(`${API}/api/inventory/stock-ledger/deficit-items`, { params });
      setDeficitData(res.data || []);
      setDeficitTotal(res.total || 0);
      setDeficitPage(pg);
    } catch { toast.error("Failed to load deficit items"); }
    finally { setDeficitLoading(false); }
  }, [dateFrom, dateTo, pageSize]);

  useEffect(() => { if (viewMode === "deficit") fetchDeficit(1); }, [viewMode, fetchDeficit]);

  const handleDeficitItemClick = (item) => {
    setSelectedItem({ itemCode: item.itemCode, itemName: item.itemName });
    setSearchQuery(item.itemName);
    setViewMode("ledger");
  };

  const totalPages = Math.ceil(total / pageSize) || 0;

  return (
    <div className="space-y-5" data-testid="stock-ledger">
      {/* ── Search + Filters ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Stock Ledger — Item Transaction History</h3>
        <div className="flex flex-wrap gap-3 items-end">
          {/* Item search */}
          <div className="relative flex-1 min-w-[280px]">
            <label className="text-[11px] font-bold text-slate-500 uppercase mb-1 block">Search Item</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text" value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); if (!e.target.value) setSelectedItem(null); }}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Type item name or code..."
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                data-testid="ledger-item-search"
              />
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {suggestions.map((item, i) => (
                  <button key={`${item.itemCode}-${i}`} onClick={() => selectItem(item)}
                    className="w-full text-left px-4 py-2.5 hover:bg-purple-50 text-sm border-b border-slate-100 last:border-0"
                    data-testid={`ledger-suggestion-${i}`}>
                    <span className="font-medium text-slate-800">{item.itemName}</span>
                    <span className="text-xs text-slate-400 ml-2">{item.itemCode}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-[11px] font-bold text-slate-500 uppercase mb-1 block">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="ledger-date-from" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-500 uppercase mb-1 block">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="ledger-date-to" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-500 uppercase mb-1 block">Movement</label>
            <select value={movementType} onChange={(e) => setMovementType(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="ledger-movement-type">
              <option value="">All Types</option>
              {Object.entries(MOVEMENT_COLORS).map(([k, v]) => (
                <option key={k} value={k}>{v.label} ({k})</option>
              ))}
            </select>
          </div>
        </div>

        {/* View mode toggle */}
        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
          <button onClick={() => setViewMode("ledger")}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${viewMode === "ledger" ? "bg-purple-600 text-white shadow-md" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="view-mode-ledger">
            <Search className="w-4 h-4 inline mr-1.5 -mt-0.5" />Item Ledger
          </button>
          <button onClick={() => setViewMode("deficit")}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${viewMode === "deficit" ? "bg-rose-600 text-white shadow-md" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="view-mode-deficit">
            <AlertTriangle className="w-4 h-4 inline mr-1.5 -mt-0.5" />Deficit Items
          </button>
        </div>
      </div>

      {/* ═══ DEFICIT VIEW ═══ */}
      {viewMode === "deficit" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h4 className="text-sm font-bold text-slate-800">Deficit Items — Consumption Exceeds Receipts</h4>
              <span className="text-xs text-slate-400">Items where total issued qty is more than total received qty{(dateFrom || dateTo) ? ` (${dateFrom || "..."} to ${dateTo || "..."})` : " (all time)"}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="bg-slate-100 px-2.5 py-1 rounded-md font-medium" data-testid="deficit-record-info">
                {Math.min((deficitPage - 1) * pageSize + 1, deficitTotal)}–{Math.min(deficitPage * pageSize, deficitTotal)} of {deficitTotal.toLocaleString("en-IN")}
              </span>
              <Button size="sm" variant="outline" disabled={deficitPage <= 1} onClick={() => fetchDeficit(deficitPage - 1)} data-testid="deficit-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" disabled={deficitPage >= Math.ceil(deficitTotal / pageSize)} onClick={() => fetchDeficit(deficitPage + 1)} data-testid="deficit-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Item Code</TableHead>
                  <TableHead className="font-semibold">Item Name</TableHead>
                  <TableHead className="font-semibold text-right text-emerald-700">Total Received</TableHead>
                  <TableHead className="font-semibold text-right text-rose-700">Total Issued</TableHead>
                  <TableHead className="font-semibold text-right text-red-700">Deficit</TableHead>
                  <TableHead className="font-semibold text-right">Transactions</TableHead>
                  <TableHead className="font-semibold w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deficitLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : deficitData.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No deficit items found for the selected date range</TableCell></TableRow>
                ) : (
                  deficitData.map((item, i) => (
                    <TableRow key={i} className="hover:bg-slate-50 cursor-pointer" onClick={() => handleDeficitItemClick(item)} data-testid={`deficit-row-${i}`}>
                      <TableCell className="font-mono text-xs text-slate-500">{item.itemCode}</TableCell>
                      <TableCell className="text-sm font-medium text-slate-800">{item.itemName}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-emerald-600">+{item.totalReceipt.toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-rose-600">-{item.totalWithdraw.toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-black">
                          <AlertTriangle className="w-3 h-3" />{item.deficit.toLocaleString("en-IN")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-600">{item.txnCount.toLocaleString("en-IN")}</TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ═══ LEDGER VIEW ═══ */}
      {viewMode === "ledger" && (<>

      {/* ── KPI Cards ── */}
      {selectedItem && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {(kpis.openingBalance != null && (dateFrom || dateTo)) && (
            <div className="bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl p-4 text-white shadow-lg relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/10" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-slate-300" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300">Opening Balance</span>
                </div>
                <p className="text-2xl font-black" data-testid="ledger-kpi-opening">{formatCurrency(kpis.openingBalance)}</p>
              </div>
            </div>
          )}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg relative overflow-hidden">
            <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/10" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-purple-200" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-purple-200">Current Balance</span>
              </div>
              <p className="text-2xl font-black" data-testid="ledger-kpi-balance">{formatCurrency(kpis.currentBalance)}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 text-white shadow-lg relative overflow-hidden">
            <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/10" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDown className="w-4 h-4 text-emerald-200" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-200">Total Received</span>
              </div>
              <p className="text-2xl font-black" data-testid="ledger-kpi-received">{formatCurrency(kpis.totalReceipt)}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl p-4 text-white shadow-lg relative overflow-hidden">
            <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/10" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUp className="w-4 h-4 text-rose-200" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-rose-200">Total Issued</span>
              </div>
              <p className="text-2xl font-black" data-testid="ledger-kpi-issued">{formatCurrency(kpis.totalWithdraw)}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl p-4 text-white shadow-lg relative overflow-hidden">
            <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/10" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Transactions</span>
              </div>
              <p className="text-2xl font-black" data-testid="ledger-kpi-txns">{formatCurrency(kpis.txnCount)}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {!selectedItem && (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Search className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">Search and select an item above to view its stock ledger</p>
          <p className="text-xs text-slate-400 mt-1">The ledger shows all receipts, consumption, adjustments chronologically with running balance</p>
        </div>
      )}

      {/* ── Ledger Table ── */}
      {selectedItem && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h4 className="text-sm font-bold text-slate-800" data-testid="ledger-item-title">{selectedItem.itemName}</h4>
              <span className="text-xs text-slate-400">{selectedItem.itemCode} — <strong className="text-slate-600">{total.toLocaleString("en-IN")}</strong> transactions</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <Button size="sm" variant="outline" onClick={handleExport} className="gap-1.5" data-testid="ledger-export-btn">
                <Download className="w-4 h-4" /> Export CSV
              </Button>
              <span className="bg-slate-100 px-2.5 py-1 rounded-md font-medium" data-testid="ledger-record-info">
                {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total.toLocaleString("en-IN")}
              </span>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchLedger(page - 1)} data-testid="ledger-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => fetchLedger(page + 1)} data-testid="ledger-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold w-[170px]">Date</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold text-right text-emerald-700">Receipt</TableHead>
                  <TableHead className="font-semibold text-right text-rose-700">Issue</TableHead>
                  <TableHead className="font-semibold text-right">Balance</TableHead>
                  <TableHead className="font-semibold">Patient</TableHead>
                  <TableHead className="font-semibold">MRN</TableHead>
                  <TableHead className="font-semibold">Bill No</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Performed By</TableHead>
                  <TableHead className="font-semibold">Dept</TableHead>
                  <TableHead className="font-semibold">Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 12 }).map((_, j) => (
                        <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : data.length === 0 ? (
                  <TableRow><TableCell colSpan={12} className="text-center text-slate-400 py-8">No transactions found for the selected filters</TableCell></TableRow>
                ) : (
                  data.map((row, i) => {
                    const mc = MOVEMENT_COLORS[row.transactionType] || { bg: "bg-slate-100", text: "text-slate-600", label: row.movementLabel };
                    const isReceipt = row.receipt > 0;
                    const isWithdraw = row.withdraw > 0;
                    return (
                      <TableRow key={i} className="hover:bg-slate-50" data-testid={`ledger-row-${i}`}>
                        <TableCell className="text-xs text-slate-600 whitespace-nowrap">{row.date}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${mc.bg} ${mc.text}`}>
                            {isReceipt ? <TrendingDown className="w-3 h-3" /> : isWithdraw ? <TrendingUp className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                            {mc.label}
                          </span>
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold tabular-nums ${isReceipt ? "text-emerald-600" : "text-slate-300"}`}>
                          {isReceipt ? `+${row.receipt}` : "-"}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold tabular-nums ${isWithdraw ? "text-rose-600" : "text-slate-300"}`}>
                          {isWithdraw ? `-${row.withdraw}` : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono font-black tabular-nums text-slate-900">{row.stockInHand}</TableCell>
                        <TableCell className="text-xs font-medium text-slate-800">{row.patientName || "-"}</TableCell>
                        <TableCell className="text-xs font-mono text-slate-500">{row.mrn || "-"}</TableCell>
                        <TableCell className="text-xs font-mono text-indigo-600">{row.billNo || "-"}</TableCell>
                        <TableCell className="text-xs">
                          {row.paymentStatus ? (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              row.paymentStatus === "PAID" ? "bg-emerald-100 text-emerald-700" :
                              row.paymentStatus === "UNPAID" ? "bg-red-100 text-red-700" :
                              "bg-amber-100 text-amber-700"
                            }`}>{row.paymentStatus}</span>
                          ) : "-"}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{row.performedBy || "-"}</TableCell>
                        <TableCell className="text-xs text-slate-600">{row.department || "-"}</TableCell>
                        <TableCell className="text-xs text-slate-500 min-w-[250px]">{row.remarks || "-"}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
